import {
    Box,
    Button,
    Divider,
    FormControlLabel,
    Grid2 as Grid,
    Input,
    Radio,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import copy from "fast-copy";
import moment, { Moment } from "moment";
import "moment-timezone";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux";
import {
    ExchangeData,
    type ExchangeRate,
    useGetCurrencyQuery,
    useLazyGetExchangeRatesQuery,
} from "../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { HighlightSelectBox, MyPaper } from "../../../components";
import { SelectBoxOption } from "../../../interfaces";
import { getUserSelectedCurrency, sortByKey } from "../../../utils";
import { useLazyGetAllCostingQuery } from "../../../redux/services/pricing";
import { useParams } from "react-router-dom";
import { closeDialog } from "../../../redux/slices/dialog";
import { Costing } from "pdf-generator";

export const USD = { label: "USD", value: "USD" };

export const ExchangeRatesSelection = ({ onCalculate }: { onCalculate: (exchange_rates: ExchangeData) => void }) => {
    const dispatch = useAppDispatch();
    const { id = "" } = useParams();
    const { enqueueSnackbar } = useSnackbar();

    const isEdit = id && id !== "New";

    const [getExchangeRates] = useLazyGetExchangeRatesQuery();
    const [getAllCosting] = useLazyGetAllCostingQuery();
    const currencyData = useGetCurrencyQuery(null);

    const { pricing, auth } = useAppSelector(store => store);

    const DEFAULT_CURRENCY = getUserSelectedCurrency(auth.userPreferences).value;

    const [disableCalculate, setDisableCalculate] = useState(false);

    const theme = useTheme();

    const isIpadOrMobileMatch = useMediaQuery(theme.breakpoints.down("md"));

    const isLaptopMatch = useMediaQuery(theme.breakpoints.up("md"));

    const [currency, setCurrency] = React.useState<SelectBoxOption | null>({ label: DEFAULT_CURRENCY, value: DEFAULT_CURRENCY });

    const [exchange_rates, setExchangeRates] = React.useState<ExchangeData>({
        base_currency: `${currency?.value ?? DEFAULT_CURRENCY}`,
        current_date: moment(),
        exchange_date: moment(),
        manual_rate: false,
        exchange_rates_auto: [],
        exchange_rates_manual: [],
        exchange_rates: [],
    });

    const prePopulateWhenError = (skip: boolean = false) => {
        let newExchangeRates: ExchangeRate[] = [];
        if (currencyData?.data) {
            for (let i = 0; i < currencyData.data.length; i++) {
                newExchangeRates[i] = {
                    manual_rate: 0,
                    market_rate: 0,
                    currency_code: currencyData?.data[i]?.currency_code || DEFAULT_CURRENCY,
                };
            }
        }
        setExchangeRates({
            ...exchange_rates,
            exchange_rates_auto: [],
            exchange_rates_manual: skip
                ? []
                : sortByKey(newExchangeRates, "currency_code"),
        });
    };

    const getCosting = async () => {
        if (isEdit && pricing.basic_details?.bom_id?.value) {
            dispatch(openBackdrop("Fetching Rates...."));
            await getAllCosting({ bom_id: pricing.basic_details?.bom_id?.value as string, pricing_id: id })
                .unwrap()
                .then(async (res) => {
                    if (res.length > 0) {
                        const clientTimezone = moment.tz.guess(); // get the client's timezone
                        const latestCosting = res?.filter((d: any) => new Date(d.created_time) > new Date(pricing.modified_time));
                        const mostRecentCosting: Partial<Costing> = latestCosting?.reduce((recent: any, current: any) => {
                            return new Date(current.created_time) > new Date(recent.created_time) ? current : recent;
                        }, latestCosting?.[0] || {});

                        if (Object.keys(mostRecentCosting || {}).length > 0) {
                            const config = mostRecentCosting?.pricing_config;
                            if ((config?.exchange_rates?.exchange_rates ?? [])?.length > 0 && config?.exchange_rates?.base_currency) {
                                setExchangeRates({
                                    ...exchange_rates,
                                    ...(config?.exchange_rates ?? []),
                                    current_date: config?.exchange_rates?.exchange_date
                                        ? moment.tz(config?.exchange_rates?.exchange_date, clientTimezone)
                                        : exchange_rates?.current_date,
                                    exchange_date: config?.exchange_rates?.exchange_date
                                        ? moment.tz(config?.exchange_rates?.exchange_date, clientTimezone)
                                        : exchange_rates?.exchange_date,
                                    exchange_rates_auto: sortByKey(
                                        config?.exchange_rates?.exchange_rates ?? [],
                                        "currency_code"
                                    ),
                                    exchange_rates_manual: sortByKey(
                                        config?.exchange_rates?.exchange_rates ?? [],
                                        "currency_code"
                                    ),
                                });
                                setCurrency(currencyData?.data?.find(
                                    (c: any) => config?.exchange_rates?.base_currency === c?.currency_code
                                ) || null);
                                enqueueSnackbar("Fetched Exchange Rates", { variant: "success" });
                                setDisableCalculate(false);
                            } else {
                                await findRates(false);
                            }
                        } else {
                            await findRates(false);
                        }

                    } else {
                        await findRates(false);
                    }
                })
                .catch((err) => {
                    prePopulateWhenError(true);
                    enqueueSnackbar(
                        "Something went wrong, Unable to fetch exchange rates",
                        { variant: "error" }
                    );
                });
            dispatch(closeBackdrop());
        } else {
            await findRates(false);
        }
    };

    const findRates = async (isManual: boolean) => {
        dispatch(openBackdrop("Fetching Rates...."));
        await getExchangeRates({
            date: `${(exchange_rates.current_date as Moment)?.utc(true).toISOString()?.split("T")?.[0] ??
                ""
                }`,
            base_currency: (currency as any)?.currency_code || exchange_rates.base_currency,
        })
            .unwrap()
            .then((res) => {
                if (res) {
                    // const clientTimezone = moment.tz.guess(); // get the client's timezone
                    const exchange_rates_manual = isManual
                        ? {
                            exchange_rates_manual: sortByKey(
                                res?.exchange_rates.map((e: ExchangeRate) => ({
                                    ...e,
                                    manual_rate: e?.market_rate,
                                })),
                                "currency_code"
                            ),
                        }
                        : {};
                    const exchange_rates_auto = isManual
                        ? {}
                        : {
                            exchange_rates_auto: sortByKey(
                                res?.exchange_rates,
                                "currency_code"
                            ),
                        };
                    setExchangeRates({
                        ...exchange_rates,
                        // current_date: moment.tz(res.exchange_date, clientTimezone),
                        // exchange_date: moment.tz(res.exchange_date, clientTimezone),
                        ...exchange_rates_manual,
                        ...exchange_rates_auto,
                        manual_rate: exchange_rates?.manual_rate || false,
                        base_currency: (currency as any)?.currency_code || exchange_rates.base_currency
                    });
                    setCurrency(currencyData?.data?.find(
                        (c) => res?.base_currency === c?.currency_code
                    ) || null);
                    enqueueSnackbar("Fetched Exchange Rates", { variant: "success" });
                    setDisableCalculate(false);
                } else {
                    if (exchange_rates?.manual_rate) {
                        setDisableCalculate(false);
                    }
                    prePopulateWhenError();
                    enqueueSnackbar(
                        "Something went wrong, Unable to find exchange rates",
                        { variant: "error" }
                    );
                }
            })
            .catch((err) => {
                console.error(`Error finding Rates: ${JSON.stringify(err)}`);
                if (exchange_rates?.manual_rate) {
                    setDisableCalculate(false);
                }
                prePopulateWhenError();
                enqueueSnackbar(
                    "Something went wrong, Unable to find exchange rates",
                    { variant: "error" }
                );
            });
        dispatch(closeBackdrop());
    };

    const updateManualExchangeRates = (index: number, value: number) => {
        let newExchangeRates = copy(exchange_rates.exchange_rates_manual);
        newExchangeRates[index].manual_rate = value;
        setExchangeRates({
            ...exchange_rates,
            exchange_rates_manual: [...newExchangeRates],
        });
    };

    const calculateCost = async () => {
        const { exchange_rates_manual, exchange_rates_auto } = exchange_rates;
        if (exchange_rates.manual_rate) {
            exchange_rates.exchange_rates = exchange_rates_manual;
        } else {
            exchange_rates.exchange_rates = exchange_rates_auto;
        }
        exchange_rates.base_currency = (currency?.value as string) || exchange_rates?.base_currency;
        onCalculate({
            base_currency: exchange_rates.base_currency,
            current_date: (exchange_rates.current_date as Moment)?.utc(true).format('YYYY-MM-DD'),
            exchange_date: (exchange_rates.exchange_date as Moment)?.utc(true).format('YYYY-MM-DD'),
            manual_rate: exchange_rates.manual_rate,
            exchange_rates_auto: exchange_rates.exchange_rates_auto,
            exchange_rates_manual: exchange_rates.exchange_rates_manual,
            exchange_rates: exchange_rates.exchange_rates,
        });
        dispatch(closeDialog());
    };

    useEffect(() => {
        if (pricing.basic_details?.bom_id?.value && currencyData.isSuccess) {
            getCosting();
        } else {
            enqueueSnackbar("Please choose BOM to find exchange Rates", { variant: "warning" })
        }
        // eslint-disable-next-line
    }, [currencyData.status]);

    return (
        (<Box>
            <Stack sx={{ position: "sticky", top: 0, zIndex: 10 }}>
                <MyPaper>
                    <>
                        <Grid
                            container
                            sx={{
                                pt: 2,
                                pl: 2,
                                columnGap: 2
                            }}>
                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 5,
                                    md: 3
                                }}>
                                {/* Date */}
                                <DatePicker
                                    value={exchange_rates.current_date as Moment}
                                    onChange={(current_date) => {
                                        setExchangeRates({
                                            ...exchange_rates,
                                            current_date,
                                            exchange_date: current_date,
                                        });
                                        setDisableCalculate(true);
                                    }}
                                    label="Date"
                                    format="DD-MM-YYYY"
                                    formatDensity="spacious"
                                    sx={{ width: "100%" }}
                                />
                            </Grid>

                            <Grid
                                size={{
                                    xs: 12,
                                    sm: 5,
                                    md: 3
                                }}>
                                <HighlightSelectBox
                                    disableClearable
                                    size="medium"
                                    label={"Base Currency"}
                                    margin={"none"}
                                    fullWdith
                                    loading={currencyData.isLoading}
                                    value={currency || null}
                                    options={
                                        sortByKey(currencyData?.data || [], "currency_code") ?? []
                                    }
                                    onChange={(data: SelectBoxOption) => {
                                        setCurrency(data);
                                        setDisableCalculate(true);
                                    }}
                                />
                            </Grid>
                            <Grid
                                container
                                sx={{
                                    mt: 3
                                }}
                                size={12}>
                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 6
                                    }}>
                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        gap={1}
                                        sx={{
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}>
                                        <FormControlLabel
                                            label={"Automatic - Market Price"}
                                            control={
                                                <Radio
                                                    checked={!exchange_rates.manual_rate}
                                                    onChange={(e) => {
                                                        setExchangeRates({
                                                            ...exchange_rates,
                                                            manual_rate: false,
                                                        });
                                                        setDisableCalculate(true);
                                                    }}
                                                />
                                            }
                                        />
                                        <Box>
                                            <Button
                                                size="small"
                                                variant="contained"
                                                disabled={exchange_rates.manual_rate}
                                                onClick={() => findRates(false)}
                                            >
                                                Find Rates
                                            </Button>
                                        </Box>
                                    </Stack>
                                </Grid>

                                <Grid
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 6
                                    }}>
                                    <Stack
                                        direction={{ xs: "column", sm: "row" }}
                                        gap={1}
                                        sx={{
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}>
                                        <FormControlLabel
                                            label={"Manual"}
                                            control={
                                                <Radio
                                                    checked={exchange_rates.manual_rate}
                                                    onChange={(e) => {
                                                        setExchangeRates({
                                                            ...exchange_rates,
                                                            manual_rate: true,
                                                        });
                                                        setDisableCalculate(true);
                                                    }}
                                                />
                                            }
                                        />

                                        <Button
                                            size="small"
                                            variant="contained"
                                            disabled={!exchange_rates.manual_rate}
                                            onClick={() => findRates(true)}
                                        >
                                            Import Current Rate
                                        </Button>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Grid>
                    </>
                </MyPaper>
            </Stack>
            <Grid container columns={13} id="grid" sx={{
                mt: 2
            }}>
                {((isIpadOrMobileMatch && !exchange_rates.manual_rate) || isLaptopMatch) &&
                    <Grid
                        sx={{
                            width: "100%"
                        }}
                        size={{
                            sm: 13,
                            md: 6
                        }}>
                        {/* Exchange Rates */}
                        <Stack
                            spacing={1}
                            sx={{
                                mt: 2,
                                width: "100%",
                                justifyContent: "center",
                                alignItems: "center"
                            }}>
                            {exchange_rates.exchange_rates_auto?.map((rate, index) => (
                                <Stack key={index} direction={"row"} spacing={1}>
                                    <Typography>
                                        <b>1</b>&nbsp;
                                        <Typography variant="caption">
                                            {currency?.value}
                                        </Typography>
                                    </Typography>
                                    <Typography>=</Typography>

                                    <Input
                                        type={"number"}
                                        onWheel={(e) => (e.target as HTMLElement).blur()}
                                        value={rate.market_rate}
                                        disabled={true}
                                        endAdornment={<>{rate.currency_code}</>}
                                    />
                                </Stack>
                            ))}
                        </Stack>
                    </Grid>
                }

                {isLaptopMatch &&
                    <Grid
                        sx={{
                            pr: 2,
                            width: "100%"
                        }}
                        size={{
                            sm: 13,
                            md: 1
                        }}>
                        <Divider
                            orientation={"vertical"}
                        />
                    </Grid>
                }

                {((isIpadOrMobileMatch && exchange_rates.manual_rate) || isLaptopMatch) &&
                    <Grid
                        sx={{
                            width: "100%"
                        }}
                        size={{
                            sm: 13,
                            md: 6
                        }}>
                        {/* Exchange Rates */}
                        <Stack
                            spacing={1}
                            sx={{
                                mt: 2,
                                width: "inherit",
                                justifyContent: "center",
                                alignItems: "center"
                            }}>
                            {exchange_rates.exchange_rates_manual?.map((rate, index) => (
                                <Stack key={index} direction={"row"} spacing={1}>
                                    <Typography>
                                        <b>1</b>&nbsp;
                                        <Typography variant="caption">
                                            {currency?.value}
                                        </Typography>
                                    </Typography>
                                    <Typography>=</Typography>

                                    <Input
                                        type={"number"}
                                        onWheel={(e) => (e.target as HTMLElement).blur()}
                                        value={rate?.manual_rate}
                                        disabled={!exchange_rates.manual_rate}
                                        onChange={(e) =>
                                            updateManualExchangeRates(index, parseFloat(e.target.value))
                                        }
                                        endAdornment={<>{rate.currency_code}</>}
                                    />
                                </Stack>
                            ))}
                        </Stack>
                    </Grid>
                }
            </Grid>
            {/* Calculate Cost Button */}
            <Box sx={{ position: "sticky", bottom: 0, p: 0 }}>
                <MyPaper>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            flexDirection: "column"
                        }}>
                        <Button
                            onClick={() => calculateCost()}
                            variant="contained"
                            disabled={
                                !exchange_rates?.current_date ||
                                !exchange_rates?.base_currency ||
                                !currency ||
                                disableCalculate
                            }
                        >
                            Update
                        </Button>
                    </Box>
                </MyPaper>
            </Box>
        </Box>)
    );
};
